import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import DatePicker from '../ui/DatePicker';
import { API_BASE_URL } from '../../api/api';

const CONTENT_TYPE_OPTIONS = [
  { value: 'story', label: 'Hikaye' },
  { value: 'post', label: 'Post' },
  { value: 'reels', label: 'Reels' },
  { value: 'shorts', label: 'Shorts' },
  { value: 'video', label: 'Video' },
  { value: 'carousel', label: 'Karosel' },
];
const ACCOUNT_NAME_OPTIONS = ['Antedon', 'Alinda', 'Claros', 'Residence', 'Lush', 'Suites'];
const PHOTO_GROUP_OPTIONS = [
  { value: 'post_grid', label: 'Post Grid' },
  { value: 'original', label: 'Original' },
  { value: 'approved', label: 'Approved Filter' },
];

const defaultState = {
  channel: '',
  content_name: '',
  account_name: '',
  share_date: '',
  responsible_person: '',
  post_theme: '',
  hashtag: '',
  description: '',
  content_type: 'post',
};

const ContentModal = ({ isOpen, onClose, onSave, onDeletePhoto, initialData, referenceData }) => {
  const [formData, setFormData] = useState(defaultState);
  const [responsibleSearch, setResponsibleSearch] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPhotoGroup, setSelectedPhotoGroup] = useState('post_grid');

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      const initialResponsibleLabel =
        initialData.responsible_person_name ||
        initialData.responsible_person?.name ||
        initialData.responsible_person?.full_name ||
        initialData.responsible_person?.email ||
        '';
      setFormData({
        channel: initialData.channel?.id || initialData.channel || '',
        content_name: initialData.content_name || '',
        account_name: initialData.account_name || '',
        share_date: initialData.share_date || '',
        responsible_person: initialData.responsible_person?.id || initialData.responsible_person || '',
        post_theme: initialData.post_theme || '',
        hashtag: initialData.hashtag || '',
        description: initialData.description || '',
        content_type: initialData.content_type || 'post',
      });
      setResponsibleSearch(initialResponsibleLabel);
    } else {
      setFormData(defaultState);
      setResponsibleSearch('');
    }
    setSelectedFiles([]);
    setSelectedPhotoGroup('post_grid');
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: initialData?.id,
      channel: formData.channel ? Number(formData.channel) : null,
      responsible_person: formData.responsible_person ? Number(formData.responsible_person) : null,
    }, selectedFiles, selectedPhotoGroup);
  };

  const channels = referenceData?.channels || [];
  const users = referenceData?.users || [];
  const filteredUsers = users.filter((user) => {
    const keyword = responsibleSearch.trim().toLowerCase();
    if (!keyword) return true;
    const label = String(user.name || user.full_name || user.email || '').toLowerCase();
    return label.includes(keyword);
  });

  const handleResponsibleLookupChange = (value) => {
    setResponsibleSearch(value);
    const matchedUser = users.find((user) => {
      const label = String(user.name || user.full_name || user.email || '');
      return label.toLowerCase() === value.trim().toLowerCase();
    });
    setFormData((prev) => ({
      ...prev,
      responsible_person: matchedUser ? matchedUser.id : '',
    }));
  };

  const uploadedPhotos = initialData?.photos || [];
  const groupedPhotos = {
    post_grid: uploadedPhotos.filter((item) => item.photo_group === 'post_grid'),
    original: uploadedPhotos.filter((item) => item.photo_group === 'original'),
    approved: uploadedPhotos.filter((item) => item.photo_group === 'approved'),
  };
  const backendBaseUrl = API_BASE_URL.replace(/\/api$/, '');

  const resolvePhotoUrl = (photo) => {
    if (!photo?.file_url) return '';
    if (photo.file_url.startsWith('http://') || photo.file_url.startsWith('https://')) return photo.file_url;
    return `${backendBaseUrl}${photo.file_url}`;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-20 px-4 overflow-hidden">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {initialData ? 'Content Düzenle' : 'Yeni Content Oluştur'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form id="content-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Kanal</Label>
                <select
                  value={formData.channel}
                  onChange={(e) => setFormData((prev) => ({ ...prev, channel: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Seçiniz</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.channel_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label>İçerik Adı</Label>
                <Input
                  value={formData.content_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content_name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-1">
                <Label>Paylaşım Tarihi</Label>
                <DatePicker
                  value={formData.share_date}
                  onChange={(e) => setFormData((prev) => ({ ...prev, share_date: e.target?.value || '' }))}
                  placeholder="Tarih Seç"
                  name="share_date"
                />
              </div>

              <div className="space-y-1">
                <Label>Account Name</Label>
                <select
                  value={formData.account_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, account_name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Seçiniz</option>
                  {ACCOUNT_NAME_OPTIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label>İçerik Tipi</Label>
                <select
                  value={formData.content_type}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content_type: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {CONTENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1 md:col-span-2">
                <Label>Sorumlu Kişi</Label>
                <Input
                  value={responsibleSearch}
                  onChange={(e) => handleResponsibleLookupChange(e.target.value)}
                  placeholder="Sorumlu kişi ara..."
                  list="responsible-person-options"
                />
                <datalist id="responsible-person-options">
                  {filteredUsers.map((user) => (
                    <option key={user.id} value={user.name || user.full_name || user.email} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-1 md:col-span-2">
                <Label>Post Metini</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="space-y-1">
                <Label>Post Teması</Label>
                <Input
                  value={formData.post_theme}
                  onChange={(e) => setFormData((prev) => ({ ...prev, post_theme: e.target.value }))}
                  placeholder="Örn: Ala Carte"
                />
              </div>

              <div className="space-y-1">
                <Label>Hashtag</Label>
                <Input
                  value={formData.hashtag}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hashtag: e.target.value }))}
                  placeholder="#AkkaHotels #Summer"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Fotoğraflar</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block">Fotoğraf Grubu</Label>
                    <select
                      value={selectedPhotoGroup}
                      onChange={(e) => setSelectedPhotoGroup(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {PHOTO_GROUP_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files || []))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Seçilen fotoğraf sayısı: {selectedFiles.length}
                  </p>
                )}

                {uploadedPhotos.length > 0 && (
                  <div className="space-y-4">
                    {PHOTO_GROUP_OPTIONS.map((group) => (
                      <div key={group.value} className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{group.label}</h4>
                        {groupedPhotos[group.value].length === 0 ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400">Bu grupta görsel yok.</p>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {groupedPhotos[group.value].map((photo) => (
                              <div key={photo.id} className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                                <img
                                  src={resolvePhotoUrl(photo)}
                                  alt={photo.file_name || 'content-photo'}
                                  className="w-full h-40 object-cover bg-gray-100 dark:bg-gray-700"
                                />
                                {onDeletePhoto && (
                                  <button
                                    type="button"
                                    onClick={() => onDeletePhoto(photo.id)}
                                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white text-xs px-2 py-1 rounded"
                                  >
                                    Sil
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose}>
            İptal
          </Button>
          <Button type="submit" form="content-form">
            Kaydet
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContentModal;

